import React from "react";

import PageHero from "components/site/PageHero";
import RevealBlock from "components/site/RevealBlock";
import SiteAction from "components/site/SiteAction";
import { Col, Container, Row } from "components/site/SitePrimitives";
import SiteShell from "components/site/SiteShell";
import {
  buildStructuredData,
  scePage,
} from "content/siteContent";
import usePageMeta from "hooks/usePageMeta";

function renderAction(action) {
  return (
    <SiteAction
      className={`site-btn ${
        action.variant === "primary" ? "site-btn-primary" : "site-btn-secondary"
      }`}
      key={action.label}
      to={action.to}
    >
      {action.label}
    </SiteAction>
  );
}

function SyntheticCognitionEngine() {
  usePageMeta({
    ...scePage.meta,
    structuredData: buildStructuredData({
      pageName: "Synthetic Cognition Engine",
      description: scePage.meta.description,
      path: scePage.meta.path,
    }),
  });

  return (
    <SiteShell pageClassName="ignitum-site-page ignitum-sce-page">
      <PageHero
        actions={scePage.hero.actions}
        aside={
          <div className="site-sce-panel">
            <div className="site-status-row">
              {scePage.hero.status.map((item) => (
                <span className="site-status-pill" key={item}>
                  {item}
                </span>
              ))}
            </div>
            <p className="site-panel-label">{scePage.hero.panelLabel}</p>
            <p className="site-sce-panel-intro">{scePage.hero.panelTitle}</p>
            <div
              aria-hidden="true"
              className="site-sce-signal-map"
              role="presentation"
            >
              {scePage.hero.signalNodes.map((item, index) => (
                <div className="site-sce-signal-node" key={item}>
                  <span className="site-sce-signal-step">0{index + 1}</span>
                  <span className="site-sce-signal-name">{item}</span>
                </div>
              ))}
            </div>
            <div className="site-sce-highlight-list">
              {scePage.hero.highlights.map((item) => (
                <div className="site-sce-highlight-item" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
            <p className="site-panel-note">{scePage.hero.panelNote}</p>
          </div>
        }
        description={scePage.hero.description}
        eyebrow={scePage.hero.eyebrow}
        heroClassName="site-hero-sce"
        supportLine={scePage.hero.supportLine}
        title={scePage.hero.title}
      />

      <section className="site-section">
        <Container>
          <RevealBlock className="site-section-intro">
            <p className="site-eyebrow">{scePage.why.eyebrow}</p>
            <h2>{scePage.why.title}</h2>
            <p>{scePage.why.description}</p>
          </RevealBlock>
          <Row className="site-grid-gap">
            <Col lg="7">
              <RevealBlock className="site-text-block">
                {scePage.why.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </RevealBlock>
            </Col>
            <Col lg="5">
              <RevealBlock>
                <div className="site-card site-highlight">
                  <p className="site-card-label">Problem space</p>
                  <ul className="site-signal-list">
                    {scePage.why.signals.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </RevealBlock>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="site-section site-section-muted">
        <Container>
          <RevealBlock className="site-section-intro">
            <p className="site-eyebrow">{scePage.differentiators.eyebrow}</p>
            <h2>{scePage.differentiators.title}</h2>
            <p>{scePage.differentiators.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            {scePage.differentiators.items.map((item, index) => (
              <Col key={item.title} md="6">
                <RevealBlock delay={index * 70}>
                  <div className="site-card">
                    <p className="site-card-label">{item.label}</p>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <RevealBlock className="site-section-intro">
            <p className="site-eyebrow">{scePage.applicability.eyebrow}</p>
            <h2>{scePage.applicability.title}</h2>
            <p>{scePage.applicability.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            {scePage.applicability.items.map((item, index) => (
              <Col key={item.title} md="6">
                <RevealBlock delay={index * 70}>
                  <div className="site-card site-sce-domain-card">
                    <p className="site-card-label">{item.label}</p>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section site-section-muted">
        <Container>
          <RevealBlock className="site-section-intro">
            <p className="site-eyebrow">{scePage.currentState.eyebrow}</p>
            <h2>{scePage.currentState.title}</h2>
            <p>{scePage.currentState.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            <Col lg="7">
              <RevealBlock>
                <div className="site-card site-highlight">
                  <h3>{scePage.currentState.activeNow.title}</h3>
                  <ul className="site-signal-list">
                    {scePage.currentState.activeNow.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </RevealBlock>
            </Col>
            <Col lg="5">
              <RevealBlock delay={100}>
                <div className="site-card">
                  <h3>{scePage.currentState.heldBack.title}</h3>
                  <ul className="site-signal-list">
                    {scePage.currentState.heldBack.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </RevealBlock>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <RevealBlock>
            <div className="site-card site-highlight">
              <div className="site-section-intro mb-0">
                <p className="site-eyebrow">{scePage.closing.eyebrow}</p>
                <h2>{scePage.closing.title}</h2>
                <p>{scePage.closing.description}</p>
              </div>
              <div className="site-page-actions">
                {scePage.closing.actions.map((action) => renderAction(action))}
              </div>
            </div>
          </RevealBlock>
        </Container>
      </section>
    </SiteShell>
  );
}

export default SyntheticCognitionEngine;
